import { DEAL_STATUS_MAP, TODAY } from '../../constants';
import { insertDealStatusChange } from '../../services/dealActivities';
import { cancelPhase, closePhase } from '../../services/phases';
import { notify } from '../../store/feedback';
import { dealLineIncomplete, dealLines } from '../../utils/budget';
import { isoStr } from '../../utils/dates';
import { allPhasesClosed, dealOfPhase, phasesOfDeal, withCancellation } from '../../utils/phases';
import { statusDatesPatch } from './dealDefaults';
import { cancelLine, currentDeals, syncLineDates } from './lineDatesSync';

// Phase pas encore commencée : la clôture l'annule, ses dates prévues sont gardées (arbitrage du
// 05/10/2026). Sinon sa fin passe à la date du jour.
const isNotStarted = (phase, today) => !!phase?.dateDebut && phase.dateDebut > today;

const closed = (ph, today) =>
  isNotStarted(ph, today) ? withCancellation(ph, ph.dateFin, today) : { ...ph, dateFin: today };

const withClosedPhase = (client, phaseId, today) =>
  (client?.phases || []).map((ph) => (ph.id === phaseId ? closed(ph, today) : ph));

/* Clôture d'une phase projet : sa date de fin passe à aujourd'hui. Son affaire d'origine passe en
   « Terminé » seulement quand toutes les phases issues de cette affaire sont clôturées (une
   maintenance qui continue garde l'affaire « En cours »). */
export default function phaseClosureHandlers(deps, api) {
  const { appState, deals, session, setDeals, update } = deps;

  async function finishDealIfLastPhase(allDeals, phases, phase, today) {
    const aff = dealOfPhase(allDeals, phase);
    if (!aff || aff.statut === 'termine') return;
    if (!allPhasesClosed(phasesOfDeal(aff, phases), today)) return;
    // Même contrôle des lignes vendues que tout passage dans un statut gagné.
    const problemes = dealLines(aff).map(dealLineIncomplete).filter(Boolean);
    if (problemes.length) {
      const statut = DEAL_STATUS_MAP[aff.statut]?.label || aff.statut;
      notify(`L'affaire « ${aff.nom} » reste en ${statut} : ${problemes.join(' ; ')}.`);
      return;
    }
    await api.saveDealPatch(aff.id, { statut: 'termine', ...statusDatesPatch(aff, 'termine') });
    await insertDealStatusChange(aff.id, 'termine', session, today);
  }

  async function handleClosePhase(clientId, phaseId) {
    const today = isoStr(TODAY);
    const client = (appState?.clients || []).find((c) => c.id === clientId);
    const notStarted = isNotStarted(
      (client?.phases || []).find((ph) => ph.id === phaseId),
      today,
    );
    const { error } = await (notStarted ? cancelPhase : closePhase)(phaseId, today);
    if (error) return void notify('Erreur lors de la clôture : ' + error.message);
    const phases = withClosedPhase(client, phaseId, today);
    update((s) => ({
      ...s,
      clients: s.clients.map((c) => (c.id === clientId ? { ...c, phases } : c)),
    }));
    const phase = phases.find((ph) => ph.id === phaseId);
    if (!phase) return;
    const allDeals = await currentDeals(deals, api);
    // Phase annulée : ligne annulée, hors CA. Sinon la fin de la phase devient la fin du contrat
    // côté CRM et la reconduction s'arrête.
    if (notStarted) await cancelLine({ deals: allDeals, setDeals }, phase);
    else await syncLineDates({ deals: allDeals, setDeals }, phase, phase.dateDebut, today);
    const cancelled = (l) => (notStarted && l.id === phase.ligneId ? { ...l, annulee: true } : l);
    const updated = allDeals.map((a) => ({ ...a, lignes: (a.lignes || []).map(cancelled) }));
    await finishDealIfLastPhase(updated, phases, phase, today);
  }

  return { handleClosePhase };
}
